import { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  LinearProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AuthContext from '../AuthContext';
import { apiFetch } from '../api';
import DataTable from 'react-data-table-component';
import './dashboard-table.css';

const formatDurationUntil = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  const diffMs = date.getTime() - Date.now();
  if (diffMs <= 0) return 'Ready';
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return days > 0 ? `${days}d ${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`;
};

export default function DashboardPage() {
  const [tinyPopupVisible, setTinyPopupVisible] = useState(false);
  const [tinyPopupText, setTinyPopupText] = useState('');
  const [copyPressed, setCopyPressed] = useState(false);
  const [okPressed, setOkPressed] = useState(false);
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [invoiceError, setInvoiceError] = useState(null);
  const [invoiceMessage, setInvoiceMessage] = useState(null);

  // Table data + server-side pagination state
  const [logs, setLogs] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);            // 1-based for DRF
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  // separate state for upload/process overlay so table loading (fetching logs)
  // doesn't trigger the fullscreen upload overlay
  const [uploading, setUploading] = useState(false);

  // Filters / ordering (kept as your defaults)
  const [statusFilter] = useState('All');
  const [searchTerm] = useState('');
  const [order, setOrder] = useState('Newest');   // we’ll still pass this to the API

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [modalFieldA, setModalFieldA] = useState('');
  const [modalFieldB, setModalFieldB] = useState('');
  const [modalFieldC, setModalFieldC] = useState('');
  const auth = useContext(AuthContext);
  const { token } = auth || {};
  const username = auth?.user?.username || auth?.username || 'User';
  const credits = auth?.credits;
  const profileLoading = auth?.profileLoading;
  const refreshProfile = auth?.refreshProfile;
  const rememberInvoice = auth?.rememberInvoice;
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordEditing, setIsPasswordEditing] = useState(false);
  const [fileSelectedPopupVisible, setFileSelectedPopupVisible] = useState(false);
  const [fileSelectedName, setFileSelectedName] = useState('');
  // Post-file-selection action choice modal
  const [postSelectChoiceVisible, setPostSelectChoiceVisible] = useState(false);
  const [selectedPostAction, setSelectedPostAction] = useState('digitize');
  const [uploadResultPopupVisible, setUploadResultPopupVisible] = useState(false);
  const [uploadResultRows, setUploadResultRows] = useState([]);
  // raw server-returned metadata objects for each uploaded file
  const [uploadResultObjects, setUploadResultObjects] = useState([]);
  const [curveCatalog, setCurveCatalog] = useState({});
  const [curveModalOpen, setCurveModalOpen] = useState(false);
  const [curveModalLogId, setCurveModalLogId] = useState(null);
  const [curveModalLoading, setCurveModalLoading] = useState(false);
  const [curveModalError, setCurveModalError] = useState(null);
  const [selectedCurves, setSelectedCurves] = useState({});
  const [invoiceQuote, setInvoiceQuote] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [availabilityInfo, setAvailabilityInfo] = useState([]);
  const [availabilityCountdowns, setAvailabilityCountdowns] = useState([]);
  const [invoiceExpiryCountdown, setInvoiceExpiryCountdown] = useState(null);

  // initialize modalFieldA with current username and keep it read-only by default
  useEffect(() => {
    if (username) setModalFieldA(username);
  }, [username]);

  const userEmail = auth?.user?.email || '';
  useEffect(() => {
    if (userEmail) setModalFieldB(userEmail);
  }, [userEmail]);

  // Toggle handler: when saving, tries to call API to persist changes and updates AuthContext
  const handleToggleEdit = async () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }
    // Saving
    setIsEditing(false);
    setTinyPopupText('Saving...');
    setTinyPopupVisible(true);
    try {
      let updatedUser = { username: modalFieldA, email: modalFieldB };

      if (token) {
        const resp = await apiFetch('/api/auth/user/', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
          body: JSON.stringify(updatedUser),
        });
        if (resp.ok) {
          const data = await resp.json();
          updatedUser = data.user || data || updatedUser;
        }
      }

      if (auth && typeof auth.login === 'function') {
        auth.login(token, updatedUser);
      }

      setTinyPopupText('Saved');
      setTinyPopupVisible(true);
      setTimeout(() => setTinyPopupVisible(false), 3000);
    } catch (err) {
      if (auth && typeof auth.login === 'function') {
        auth.login(token, { username: modalFieldA, email: modalFieldB });
      }
      setTinyPopupText('Saved (local)');
      setTinyPopupVisible(true);
      setTimeout(() => setTinyPopupVisible(false), 3000);
    }
  };

  // ---------- SERVER-SIDE PAGINATION AWARE FETCH ----------
  const fetchLogs = useCallback(async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextPageSize = opts.pageSize ?? pageSize;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      // filters
      if (statusFilter !== 'All') params.append('status', statusFilter.toLowerCase());
      if (searchTerm) params.append('search', searchTerm);

      // ordering -> DRF ?ordering=...
      const orderingParam = order === 'Newest' ? '-created_at' : 'created_at';
      params.append('ordering', orderingParam);

      // pagination
      params.append('page', String(nextPage));
      params.append('page_size', String(nextPageSize));

      const url = `/api/logs/${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiFetch(url, {
        headers: token ? { Authorization: `Token ${token}` } : {}
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Backward-compat: API returned a plain array (not paginated)
        setLogs(data);
        setTotalRows(data.length);
      } else if (data && typeof data === 'object') {
        // DRF PageNumberPagination response: { count, next, previous, results }
        setLogs(Array.isArray(data.results) ? data.results : []);
        setTotalRows(typeof data.count === 'number' ? data.count : 0);
      } else {
        setLogs([]);
        setTotalRows(0);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to fetch logs');
      setLogs([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, searchTerm, order, token]);

  // Initial + whenever token or dependencies change
  useEffect(() => {
    if (token) {
      fetchLogs({ page: 1, pageSize }); // reset to first page on auth/filters change
      setPage(1);
    }
  }, [token, statusFilter, searchTerm, order, pageSize, fetchLogs]);

  useEffect(() => {
    const expiresAt = invoiceQuote?.expires_at;
    if (!expiresAt) {
      setInvoiceExpiryCountdown(null);
      return;
    }
    const target = new Date(expiresAt);
    const tick = () => {
      setInvoiceExpiryCountdown(formatDurationUntil(target));
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [invoiceQuote?.expires_at]);

  // react-data-table-component callbacks
  const handlePageChange = async (newPage) => {
    setPage(newPage);
    await fetchLogs({ page: newPage, pageSize });
  };

  const handleRowsPerPageChange = async (newPageSize, newPage) => {
    // RDT passes both newPageSize and current page
    setPageSize(newPageSize);
    setPage(1);
    await fetchLogs({ page: 1, pageSize: newPageSize });
  };

  // Optional: if you want to let users sort by clicking headers (server-side)
  // You can map column -> backend field here.
  const handleSort = async (column, direction) => {
    // direction is 'asc' | 'desc'
    // Map your UI columns to API fields; keeping your existing Newest/Oldest toggle for now.
    // Example if you later enable per-column sorting:
    // const apiField = column.id === 'CreatedAt' ? 'created_at' : null;
    // if (apiField) setOrder(direction === 'desc' ? 'Newest' : 'Oldest');
    // For now we no-op to keep behavior the same.
    return;
  };

  const fileInputRef = useRef(null);
  const handleFileChange = (e) => {
    console.debug('handleFileChange invoked', e?.target?.files?.length);
    const filesArray = Array.from(e.target.files || []);
    if (filesArray.length === 0) return;

    // Allow list (extensions and mime types)
    const allowedExt = /\.(tif|tiff|jpe?g|png|pdf)$/i;
    const allowedTypes = new Set([
      'image/tiff',
      'image/jpeg',
      'image/png',
      'application/pdf',
    ]);

    // Filter valid files
    const validFiles = filesArray.filter((f) => {
      if (f.type && allowedTypes.has(f.type)) return true;
      return allowedExt.test(f.name || '');
    });

    if (validFiles.length === 0) {
      setTinyPopupText('Please select only .tif .tiff .jpg .jpeg .png or .pdf files');
      setTinyPopupVisible(true);
      e.target.value = '';
      return;
    }
    setFiles(validFiles);

    const first = validFiles[0];
    setFileSelectedName(first.name || 'Selected file');
    // setFileSelectedPopupVisible(true);

    try {
      const previewRows = validFiles.map((f) => [f.name || '', f.type || 'unknown', `${Math.round((f.size || 0) / 1024)} KB`]);
      setUploadResultRows(previewRows.slice(0, 8));
      // Instead of immediately opening the file preview, show the three-option choice modal.
      console.debug('Calling handleUpload from handleFileChange');
      handleUpload(validFiles);
    } catch (err) {
      console.error('failed to create file preview rows', err);
    }

    e.target.value = '';
  };

  // Column width state for resizable columns (keyed by column id)
  const DEFAULT_MIN_WIDTH = '200px';
  const [columnWidths, setColumnWidths] = useState({
    fileName: DEFAULT_MIN_WIDTH,
    status: DEFAULT_MIN_WIDTH,
    country: DEFAULT_MIN_WIDTH,
    stateProv: DEFAULT_MIN_WIDTH,
    county: DEFAULT_MIN_WIDTH,
    api: DEFAULT_MIN_WIDTH,
    createdAt: DEFAULT_MIN_WIDTH,
    actions: DEFAULT_MIN_WIDTH,
  });

  // Selection state for rows: map of row.id -> boolean
  const [selectedRows, setSelectedRows] = useState({});

  const toggleRowSelection = (id) => {
    setSelectedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Selection state for popup tables (use index keys)
  const [uploadSelectedRows, setUploadSelectedRows] = useState({});

  // Expanded state for popup rows (index-based)
  const [uploadRowExpanded, setUploadRowExpanded] = useState({});

  const toggleUploadRowExpanded = (index) => {
    setUploadRowExpanded(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleUploadSelection = (index) => {
    setUploadSelectedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const setAllUploadSelection = (checked) => {
    const next = {};
    const subNext = {};
    const nestedDefaultCount = 3;
    for (let i = 0; i < (uploadResultRows?.length || 0); i++) {
      next[i] = !!checked;
      // determine how many nested rows exist for this main row (fallback to default)
      const existing = uploadSubSelectedRows && uploadSubSelectedRows[i] ? Object.keys(uploadSubSelectedRows[i]).length : 0;
      const count = Math.max(existing, nestedDefaultCount);
      subNext[i] = {};
      for (let j = 0; j < count; j++) subNext[i][j] = !!checked;
    }
    setUploadSelectedRows(next);
    setUploadSubSelectedRows(subNext);
  };

  // initialize upload popup selection whenever rows change
  useEffect(() => {
    const next = {};
    for (let i = 0; i < (uploadResultRows?.length || 0); i++) {
      const r = uploadResultRows[i];
      // heuristics: treat as result if any non-empty cell exists
      const hasResult = Array.isArray(r) ? r.some((c) => c !== null && c !== undefined && String(c).trim() !== '') : Boolean(r);
      next[i] = !!hasResult;
    }
    setUploadSelectedRows(next);
  }, [uploadResultRows]);

  // Selection state for nested subtable rows: map mainIndex -> { subIndex: boolean }
  const [uploadSubSelectedRows, setUploadSubSelectedRows] = useState({});

  const toggleUploadSubSelection = (mainIndex, subIndex) => {
    setUploadSubSelectedRows((prev) => {
      const next = { ...prev };
      next[mainIndex] = { ...(next[mainIndex] || {}) };
      next[mainIndex][subIndex] = !next[mainIndex][subIndex];
      return next;
    });
  };

  // initialize nested selections when uploadResultRows change (default: false)
  useEffect(() => {
    const next = {};
    for (let i = 0; i < (uploadResultRows?.length || 0); i++) {
      next[i] = {};
      // default to 3 nested rows (matches current placeholder rows)
      for (let j = 0; j < 3; j++) next[i][j] = true;
    }
    setUploadSubSelectedRows(next);
  }, [uploadResultRows]);

  const selectedCurveEntries = useMemo(() => {
    return Object.entries(selectedCurves)
      .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
      .map(([logId, ids]) => ({
        logId: Number(logId),
        curveIds: Array.from(new Set(ids)),
      }));
  }, [selectedCurves]);

  const selectedCurveDetails = useMemo(() => {
    return selectedCurveEntries.map(({ logId, curveIds }) => {
      const catalogEntry = curveCatalog[logId] || {};
      const curves = Array.isArray(catalogEntry.curves)
        ? catalogEntry.curves.filter((curve) => curveIds.includes(curve.id))
        : [];
      return {
        logId,
        curveIds,
        logLabel:
          catalogEntry['name of log'] ||
          catalogEntry.name ||
          catalogEntry.visible_name ||
          catalogEntry.file_name ||
          `Log ${logId}`,
        curves,
      };
    });
  }, [selectedCurveEntries, curveCatalog]);

  const totalSelectedCurves = useMemo(() => {
    return selectedCurveEntries.reduce((acc, entry) => acc + entry.curveIds.length, 0);
  }, [selectedCurveEntries]);

  const flattenedCurveIds = useMemo(() => {
    return Array.from(new Set(selectedCurveEntries.flatMap((entry) => entry.curveIds)));
  }, [selectedCurveEntries]);

  const fetchCurveOptions = useCallback(
    async (logId) => {
      if (!logId) return;
      setCurveModalLoading(true);
      setCurveModalError(null);
      try {
        const response = await apiFetch('/api/logs/upload_state/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Token ${token}` } : {}),
          },
          body: JSON.stringify({ ids: [logId] }),
        });

        if (!response.ok) {
          let message = 'Failed to load curves for this log.';
          try {
            const raw = await response.text();
            if (raw) {
              try {
                const parsed = JSON.parse(raw);
                if (parsed?.detail) {
                  message = parsed.detail;
                } else if (typeof parsed === 'string') {
                  message = parsed;
                }
              } catch (_) {
                message = raw;
              }
            }
          } catch (_) {
            // ignore
          }
          throw new Error(message);
        }

        const data = await response.json();
        const logKey = String(logId);
        const logEntry = data?.logs?.[logKey] || {};
        const curves = Array.isArray(logEntry.curves) ? logEntry.curves : [];
        setCurveCatalog((prev) => ({
          ...prev,
          [logId]: { ...logEntry, curves },
        }));
        if (curves.length === 0) {
          setCurveModalError('No curves are currently available for this log.');
        }
      } catch (err) {
        setCurveModalError(err?.message || 'Failed to load curves for this log.');
      } finally {
        setCurveModalLoading(false);
      }
    },
    [token]
  );

  const openCurveSelector = useCallback(
    (logId) => {
      if (!logId) return;
      setCurveModalLogId(logId);
      setCurveModalError(null);
      setCurveModalOpen(true);
      if (!curveCatalog[logId]) {
        fetchCurveOptions(logId);
      }
    },
    [curveCatalog, fetchCurveOptions]
  );

  const closeCurveSelector = useCallback(() => {
    setCurveModalOpen(false);
    setCurveModalLogId(null);
    setCurveModalError(null);
  }, []);

  const toggleCurveSelectionForLog = useCallback((logId, curveId) => {
    if (!logId || !curveId) return;
    setSelectedCurves((prev) => {
      const next = { ...prev };
      const current = new Set(next[logId] || []);
      if (current.has(curveId)) {
        current.delete(curveId);
      } else {
        current.add(curveId);
      }
      if (current.size === 0) {
        delete next[logId];
      } else {
        next[logId] = Array.from(current);
      }
      return next;
    });
  }, []);

  const selectAllCurvesForLog = useCallback((logId, curves, checked) => {
    if (!logId) return;
    setSelectedCurves((prev) => {
      const next = { ...prev };
      if (!checked) {
        delete next[logId];
        return next;
      }
      const ids = Array.isArray(curves) ? curves.map((curve) => curve.id) : [];
      if (!ids.length) {
        delete next[logId];
        return next;
      }
      next[logId] = Array.from(new Set(ids));
      return next;
    });
  }, []);

  const getLogLabel = useCallback(
    (logId) => {
      const catalogEntry = curveCatalog[logId];
      if (catalogEntry) {
        return (
          catalogEntry['name of log'] ||
          catalogEntry.name ||
          catalogEntry.visible_name ||
          catalogEntry.file_name ||
          `Log ${logId}`
        );
      }
      const logFromList = logs.find((log) => log.id === logId);
      if (logFromList) {
        const fileName =
          logFromList.visible_name ||
          logFromList.fileName ||
          (logFromList.file ? logFromList.file.split('/').pop() : '') ||
          logFromList.API ||
          null;
        return fileName || `Log ${logId}`;
      }
      return `Log ${logId}`;
    },
    [curveCatalog, logs]
  );

  const activeCurveList = useMemo(() => {
    if (!curveModalLogId) return [];
    const entry = curveCatalog[curveModalLogId];
    return Array.isArray(entry?.curves) ? entry.curves : [];
  }, [curveCatalog, curveModalLogId]);

  const handleRequestInvoice = useCallback(async () => {
    if (!flattenedCurveIds.length) {
      setInvoiceError('Select at least one curve to generate an invoice.');
      setInvoiceMessage(null);
      return;
    }

    setInvoiceLoading(true);
    setInvoiceError(null);
    setInvoiceMessage(null);
    setAvailabilityInfo([]);

    try {
      const payload = {
        curve_ids: flattenedCurveIds,
      };
      if (invoiceQuote?.external_id) {
        payload.invoice_id = invoiceQuote.external_id;
      }
      const response = await apiFetch('/api/logs/invoices/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Token ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let message = 'Unable to generate invoice.';
        try {
          const raw = await response.text();
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              if (parsed?.detail) {
                message = parsed.detail;
              } else if (parsed?.invoice_id) {
                message = Array.isArray(parsed.invoice_id)
                  ? parsed.invoice_id.join(' ')
                  : String(parsed.invoice_id);
              } else if (typeof parsed === 'string') {
                message = parsed;
              }
            } catch (_) {
              message = raw;
            }
          }
        } catch (_) {
          // ignore
        }
        throw new Error(message);
      }

      const data = await response.json();
      if (data?.invoice) {
        setInvoiceQuote(data.invoice);
        if (rememberInvoice) rememberInvoice(data.invoice);
        setInvoiceMessage('Invoice ready. Review totals before confirming your purchase.');
      } else {
        setInvoiceQuote(null);
        setInvoiceMessage(null);
      }
    } catch (err) {
      setInvoiceError(err?.message || 'Unable to generate invoice.');
    } finally {
      setInvoiceLoading(false);
    }
  }, [flattenedCurveIds, invoiceQuote?.external_id, rememberInvoice, token]);

  const handleCompletePurchase = useCallback(async () => {
    if (!invoiceQuote?.external_id) {
      setInvoiceError('No pending invoice to complete.');
      return;
    }

    setInvoiceLoading(true);
    setInvoiceError(null);
    setInvoiceMessage(null);

    try {
      const response = await apiFetch('/api/purchases/complete/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Token ${token}` } : {}),
        },
        body: JSON.stringify({ invoice_id: invoiceQuote.external_id }),
      });

      if (!response.ok) {
        let message = 'Purchase could not be completed.';
        try {
          const raw = await response.text();
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              if (parsed?.detail) {
                message = parsed.detail;
              } else if (typeof parsed === 'string') {
                message = parsed;
              }
            } catch (_) {
              message = raw;
            }
          }
        } catch (_) {
          // ignore
        }
        throw new Error(message);
      }

      const data = await response.json();
      if (data?.invoice) {
        setInvoiceQuote(data.invoice);
        if (rememberInvoice) rememberInvoice(data.invoice);
      }
      if (Array.isArray(data?.availability)) {
        setAvailabilityInfo(data.availability);
      } else {
        setAvailabilityInfo([]);
      }
      if (refreshProfile) {
        refreshProfile(token, { force: true }).catch(() => {});
      }
      setSelectedCurves({});
      setInvoiceMessage('Purchase completed. Credits have been updated.');
      setMessage('Purchase completed successfully.');
      try {
        await fetchLogs({ page, pageSize });
      } catch (_) {
        // ignore refresh failures
      }
    } catch (err) {
      setInvoiceError(err?.message || 'Purchase could not be completed.');
    } finally {
      setInvoiceLoading(false);
    }
  }, [fetchLogs, invoiceQuote?.external_id, page, pageSize, refreshProfile, rememberInvoice, token]);

  useEffect(() => {
    if (!availabilityInfo || availabilityInfo.length === 0) {
      setAvailabilityCountdowns([]);
      return;
    }
    const tick = () => {
      const now = Date.now();
      setAvailabilityCountdowns(
        availabilityInfo.map((entry) => {
          const availableDate = new Date(entry.available_after);
          const ready = availableDate.getTime() - now <= 0;
          return {
            logId: entry.log_id,
            availableAfter: availableDate,
            ready,
            countdown: formatDurationUntil(availableDate),
          };
        })
      );
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [availabilityInfo]);

  // Resizable header component (desktop mouse drag)
  const ResizableHeader = ({ columnId, children }) => {
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);
    const targetRef = useRef(null);

    const onPointerDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (err) {}
      targetRef.current = e.currentTarget;

      startXRef.current = e.clientX;
      startWidthRef.current = columnWidths[columnId] ?? 200;

      const onPointerMove = (moveEvent) => {
        const delta = moveEvent.clientX - startXRef.current;
        const newWidth = Math.max(200, Math.round(startWidthRef.current + delta));
        // eslint-disable-next-line no-console
        console.log('resizing', columnId, newWidth);
        setColumnWidths((prev) => ({ ...prev, [columnId]: newWidth }));
      };

      const onPointerUp = () => {
        try { e.target.releasePointerCapture?.(e.pointerId); } catch (err) {}
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    };

    return (
      <div className="resizable-header"
        style={{ display: 'flex', alignItems: 'center', userSelect: 'none', width: '100%', position: 'relative', boxSizing: 'border-box' }}>
        <div style={{ flex: '1 1 0%', minWidth: 0 }}>{children}</div>
        <div
          role="separator"
          aria-orientation="horizontal"
          className="separator"
          onPointerDown={onPointerDown}
          style={{position: 'absolute', left: '100%', top: '50%', transform: 'translate(-50%, -50%)', width: 12,
            height: 36, cursor: 'col-resize', touchAction: 'none', zIndex: 200000, pointerEvents: 'auto', background: 'transparent' }}>
          <div className="resize-line" />
        </div>
      </div>
    );
  };

  const tableColumns = [
    {
      id: 'FileName',
      name: <ResizableHeader columnId="FileName">File Name</ResizableHeader>,
      grow: 0,
      minWidth: DEFAULT_MIN_WIDTH,
      cell: row => (
        <div style={{ width: columnWidths['FileName'] ? `${columnWidths['FileName']}px` : undefined, boxSizing: 'border-box', overflow: 'hidden' }}>
          {row.fileName}
        </div>
      )
    },
    {
      id: 'Status',
      name: <ResizableHeader columnId="Status">Status</ResizableHeader>,
      grow: 0,
      minWidth: DEFAULT_MIN_WIDTH,
      cell: row => (
        <div style={{ width: columnWidths['Status'] ? `${columnWidths['Status']}px` : undefined, boxSizing: 'border-box', overflow: 'hidden' }}>
          {row.status}
        </div>
      )
    },
    {
      id: 'Country',
      name: <ResizableHeader columnId="Country">Country</ResizableHeader>,
      grow: 0,
      minWidth: DEFAULT_MIN_WIDTH,
      cell: row => (
        <div style={{ width: columnWidths['Country'] ? `${columnWidths['Country']}px` : undefined, boxSizing: 'border-box', overflow: 'hidden' }}>
          {row.country}
        </div>
      )
    },
    {
      id: 'StateProv',
      name: <ResizableHeader columnId="StateProv">State/Province</ResizableHeader>,
      grow: 0,
      minWidth: DEFAULT_MIN_WIDTH,
      cell: row => (
        <div style={{ width: columnWidths['StateProv'] ? `${columnWidths['StateProv']}px` : undefined, boxSizing: 'border-box', overflow: 'hidden' }}>
          {row.stateProv}
        </div>
      )
    },
    {
      id: 'County',
      name: <ResizableHeader columnId="County">County</ResizableHeader>,
      grow: 0,
      minWidth: DEFAULT_MIN_WIDTH,
      cell: row => (
        <div style={{ width: columnWidths['County'] ? `${columnWidths['County']}px` : undefined, boxSizing: 'border-box', overflow: 'hidden' }}>
          {row.County}
        </div>
      )
    },
    {
      id: 'API',
      name: <ResizableHeader columnId="API">API</ResizableHeader>,
      grow: 0,
      minWidth: DEFAULT_MIN_WIDTH,
      cell: row => (
        <div style={{ width: columnWidths['API'] ? `${columnWidths['API']}px` : undefined, boxSizing: 'border-box', overflow: 'hidden' }}>
          {row.API}
        </div>
      )
    },
    {
      id: 'CreatedAt',
      name: <ResizableHeader columnId="CreatedAt">Upload/Purchase Date</ResizableHeader>,
      grow: 0,
      minWidth: DEFAULT_MIN_WIDTH,
      sortable: true, // if you later wire handleSort mapping
      cell: row => (
        <div style={{ width: columnWidths['CreatedAt'] ? `${columnWidths['CreatedAt']}px` : undefined, boxSizing: 'border-box', overflow: 'hidden' }}>
          {row.CreatedAt ? new Date(row.CreatedAt).toLocaleDateString() : ''}
        </div>
      )
    },
    // Select checkbox column (last column)
    {
      id: 'select',
      name: <ResizableHeader columnId="select">Select</ResizableHeader>,
      grow: 0,
      minWidth: '80px',
      maxWidth: '120px',
      cell: row => {
        // initial checked heuristics: treat row as having results if common keys exist
        const hasResult = Boolean(row.results || row.result_count || (row.status && String(row.status).toLowerCase() === 'processed') || row.API);
        const checked = selectedRows[row.id] ?? hasResult;
        return (
          <div style={{ width: columnWidths['select'] ? `${columnWidths['select']}px` : undefined, textAlign: 'center', verticalAlign: 'middle' }}>
            <input
              type="checkbox"
              checked={!!checked}
              onChange={() => toggleRowSelection(row.id)}
              aria-label={`Select row ${row.id}`}
            />
          </div>
        );
      }
    },
    {
      id: 'actions',
      name: <ResizableHeader columnId="actions">Actions</ResizableHeader>,
      cell: row => (
        <Stack
          spacing={1}
          sx={{
            width: columnWidths['actions'] ? `${columnWidths['actions']}px` : 'auto',
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => openCurveSelector(row.id)}
            disabled={invoiceLoading}
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#f5f5f5', color: '#000' },
              '&:disabled': { backgroundColor: 'rgba(255,255,255,0.2)', color: 'rgba(0,0,0,0.4)' },
            }}
          >
            Select Curves
          </Button>
          <Button
            component={RouterLink}
            to={`/logs/${row.id}`}
            variant="outlined"
            size="small"
            sx={{
              borderColor: '#fff',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            View Log
          </Button>
        </Stack>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      grow: 0,
      minWidth: DEFAULT_MIN_WIDTH,
    }
  ];

  // normalize id -> width lookup helper
  const getWidthFor = (colId) => {
    return columnWidths[colId] ?? columnWidths[colId.toLowerCase()];
  };

  const columnsWithWidths = tableColumns.map((col) => {
    const w = getWidthFor(col.id);
    // create a shallow copy and remove props that should not be forwarded to DOM
    const next = { ...col };
  // remove minWidth/maxWidth to avoid React warning about unknown DOM props
  if (Object.prototype.hasOwnProperty.call(next, 'minWidth')) delete next.minWidth;
  if (Object.prototype.hasOwnProperty.call(next, 'maxWidth')) delete next.maxWidth;
  // remove other props that the table library may forward to DOM elements
  // and that React will warn about. Keep the minimal set of keys the table
  // actually needs (id, name, cell, selector, sortable, width, etc.).
  const toStrip = ['allowOverflow', 'ignoreRowClick', 'center', 'compact', 'grow', 'hide', 'right', 'draggable', 'reorder', 'wrap', 'button', 'allowOverflow'];
  toStrip.forEach((k) => { if (Object.prototype.hasOwnProperty.call(next, k)) delete next[k]; });
  // set computed width if available
  if (w) next.width = `${w}px`;
    return next;
  });

  const tableCustomStyles = {
    tableWrapper: {
      style: {
        borderCollapse: 'collapse',
        margin: 0,
        backgroundColor: '#000000'
      }
    },
    table: {
      style: {
        backgroundColor: '#000000',
        borderCollapse: 'collapse',
        border: '1px solid rgba(255, 255, 255, 1)'
      }
    },
    headRow: {
      style: {
        backgroundColor: '#000000',
        position: 'sticky',
        top: '0px',
        zIndex: 0,
        overflow: 'visible'
      }
    },
    headCells: {
      style: {
        color: '#ffffff',
        fontWeight: 400,
        fontSize: '1rem',
        paddingLeft: '1px',
        paddingRight: '1px',
        borderBottom: '1px solid rgba(255, 255, 255, 1)',
        overflow: 'visible',
        pointerEvents: 'auto',
        whiteSpace: 'nowrap'
      }
    },
    rows: {
      style: {
        backgroundColor: '#000000',
        color: '#ffffff',
        borderBottom: '1px solid rgba(255,255,255,0.05)'
      }
    },
    cells: {
      style: {
        color: '#ffffff',
        borderRight: '1px solid rgba(255,255,255,0.05)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        paddingLeft: '12px',
        paddingRight: '12px',
        overflow: 'visible'
      }
    }
  };

  // (Removed debug DOM logging and overlay injection)

  const handleUpload = async (filesParam) => {
    // allow caller to provide files directly to avoid state-update races
    const filesToUse = Array.isArray(filesParam) ? filesParam : files;
    if (!filesToUse || filesToUse.length === 0) return;
    console.debug('handleUpload starting, files count=', filesToUse.length);
    setMessage(null);
    setUploading(true);

    const formData = new FormData();
    filesToUse.forEach((file) => formData.append('files', file));

    try {
      const response = await apiFetch('/api/logs/', {
        method: 'POST',
        headers: token ? { Authorization: `Token ${token}` } : {},
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => null);
        throw new Error(text || 'Upload failed');
      }

      // parse server response if available
      let data = null;
      try {
        data = await response.json();
      } catch (_) {
        data = null;
      }

      setMessage('Upload successful');
      setFiles([]);

      try {
        // refresh page 1 to show new items
        setPage(1);
        await fetchLogs({ page: 1, pageSize });
      } catch (err) {
        console.error('fetchLogs after upload failed', err);
      }

      // prefer server-returned metadata when possible
      if (data) {
        // normalize server-returned objects into table rows and keep raw objects
        const objects = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : [data]);
        setUploadResultObjects(objects);
        const rowsFromObjects = objects.map((obj, idx) => {
          // Prefer the original filename from the user's device (filesToUse order matches objects)
          const originalName = (filesToUse && filesToUse[idx] && filesToUse[idx].name) || obj.visible_name || obj.fileName || obj.filename || (obj.file ? obj.file.split('/').pop() : '') || '';
          // Type column should contain the file extension from the original filename
          let type = '';
          const dot = originalName.lastIndexOf('.');
          if (dot !== -1) type = originalName.slice(dot + 1).toLowerCase();
          // Fallback to server-provided mime/type if no extension
          if (!type) type = obj.type || obj.mime || obj.content_type || '';

          const api = obj.api || obj.API || (obj.properties && obj.properties.api) || '';
          const country = obj.country || obj.Country || (obj.location && obj.location.country) || '';
          const stateProv = obj.state || obj.stateProv || obj.State || (obj.location && obj.location.state) || '';
          const county = obj.county || obj.County || (obj.location && obj.location.county) || '';
          const curveCount = (obj.curves && obj.curves.length) || obj.curve_count || obj.result_count || 0;
          return [originalName, type, api, country, stateProv, county, curveCount];
        });
        setUploadResultRows(rowsFromObjects);
      } else {
        const rows = [
          ['Field A1', 'Field B1', 'Field C1'],
          ['Field A2', 'Field B2', 'Field C2'],
          ['Field A3', 'Field B3', 'Field C3'],
          ['Field A4', 'Field B4', 'Field C4'],
        ];
        setUploadResultRows(rows);
      }

      setPostSelectChoiceVisible(true);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: '#000000ff',
      minHeight: '100vh',
      paddingTop: '80px',
    }}>
  {/* Fullscreen loading overlay while uploads/process are in-flight */}
  {uploading && (
        <>
          <div
            aria-hidden="true"
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.72)',
              zIndex: 6500,
            }}
          />
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 6501,
              backgroundColor: '#111',
              color: '#fff',
              padding: 24,
              borderRadius: 8,
              minWidth: 280,
              maxWidth: '90%',
              textAlign: 'center',
              boxShadow: '0 6px 24px rgba(0,0,0,0.6)'
            }}
          >
            <div style={{ marginBottom: 12, fontSize: 18, fontWeight: 600 }}>Uploading & processing files…</div>
            <LinearProgress sx={{ width: '100%' }} />
          </div>
        </>
      )}
      <Container maxWidth={false} sx={{ py: 4, mt: 5 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          color='#fff'
          sx={{
            position: 'absolute',
            left: '720px',
            top: '150px',
            zIndex: 1000,
          }}
        >
          Dashboard
        </Typography>
        {/* Add the outlined box here */}
        <Box
          sx={{
            position: 'absolute',
            top: '100px',
            left: '20px',
            border: '2px solid #fff',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            minWidth: '400px',
            minHeight: '100px',
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontSize: '2rem',
              lineHeight: 1.4,
            }}>
            {`Welcome, ${username}`}
          </Typography>
          <Typography sx={{ color: '#fff', fontSize: '1.2rem', lineHeight: 1.4 }}>
            Remaining Balance:{' '}
            {profileLoading
              ? 'Loading…'
              : credits === null || credits === undefined
              ? '—'
              : credits.toLocaleString()}
          </Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.9rem', lineHeight: 1.4 }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowLoginModal(true);
              }}
              style={{ color: '#a4fdffff', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Account Details
            </a>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {postSelectChoiceVisible && (
        <>
        {/* Backdrop overlay */}
          <div
            aria-hidden="true"
            style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.55)',
            zIndex: 4995,
            pointerEvents: 'auto'
          }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Choose action for selected files"
            style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 5000,
            backgroundColor: '#111',
            color: '#fff',
            padding: '20px 24px',
            borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
            minWidth: 560,
            maxWidth: '90%',
            textAlign: 'center'
          }}
        >
        <div style={{ marginBottom: 12, fontSize: 18, fontWeight: 600 }}>
        What would you like to do with the selected file(s)?
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => {
            // Digitize chosen -> open the existing file-selection preview popup
            setPostSelectChoiceVisible(false);
            setSelectedPostAction('digitize');
            setFileSelectedPopupVisible(true);
          }}
          style={{
            padding: '12px 18px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.12)',
            background: '#fff',
            color: '#000',
            cursor: 'pointer',
            minWidth: 180,
            fontWeight: 600
          }}
        >
          Digitize
        </button>

        <button
          disabled
          aria-disabled="true"
          // intentionally disabled until feature is implemented
          style={{
            padding: '12px 18px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.5)',
            cursor: 'not-allowed',
            minWidth: 180,
            fontWeight: 600,
            opacity: 0.7
          }}
        >
          Nomenclature standardization
        </button>

        <button
        disabled
        aria-disabled="true"
          onClick={() => {
            setPostSelectChoiceVisible(false);
            setSelectedPostAction('depth');
            // placeholder behaviour: show a small message for now
            setTinyPopupText('Generate depth registration selected (not implemented yet)');
            setTinyPopupVisible(true);
            setTimeout(() => setTinyPopupVisible(false), 3000);
          }}
          style={{
            padding: '12px 18px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.12)',
            background: '#fff',
            color: '#000',
            cursor: 'pointer',
            minWidth: 180,
            fontWeight: 600
          }}
        >
          Generate depth registration
        </button>
      </div>

      <div style={{ marginTop: 14 }}>
        <button
          onClick={() => setPostSelectChoiceVisible(false)}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'transparent',
            color: '#fff',
            cursor: 'pointer',
            marginTop: 12
          }}
        >
          Cancel
        </button>
      </div>
    </div>
    </>
    )}
        {fileSelectedPopupVisible && (
          <>
            {/* Backdrop overlay */}
            <div
              aria-hidden="true"
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.55)',
                zIndex: 4990,
                pointerEvents: 'auto'
              }}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Selected file"
              style={{
                position: 'fixed',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 5000,
                backgroundColor: '#111',
                color: '#fff',
                padding: '20px 24px',
                borderRadius: 8,
                boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
                minWidth: 1500,
                maxWidth: '90%',
                minHeight: '600px',
                textAlign: 'center'
              }}
            >
              <div style={{ marginBottom: 12, fontSize: 16, fontWeight: 500 }}>
                File(s) selected
              </div>
              {uploadResultRows && uploadResultRows.length > 0 && (
                <div style={{ overflowX: 'auto', marginBottom: 12, minHeight: '500px' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      color: '#fff',
                      textAlign: 'left',
                      marginBottom: 8,
                      tableLayout: 'fixed'
                    }}
                  >
                    <colgroup>
                      <col style={{ width: '350px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '100px' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 300}}>File Name</th>
                        <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>Type</th>
                        <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>API</th>
                        <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>Country</th>
                        <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>State/Province</th>
                        <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>County</th>
                        <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}># Curves Selected</th>
                        <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            <div style={{ fontSize: 13, color: '#fff' }}>Select All</div>
                            <input
                              type="checkbox"
                              aria-label="Select all"
                              checked={uploadResultRows && uploadResultRows.length > 0 && Object.values(uploadSelectedRows).every(Boolean)}
                              onChange={(e) => setAllUploadSelection(e.target.checked)}
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                      {uploadResultRows.map((r, i) => (
                        <tbody key={i}>
                          <tr>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <button
                                onClick={() => toggleUploadRowExpanded(i)}
                                aria-expanded={!!uploadRowExpanded[i]}
                                style={{ marginRight: 8, background: 'transparent', border: 'none', color: '#a4fdff', cursor: 'pointer' }}
                              >
                                {uploadRowExpanded[i] ? '▾' : '▸'}
                              </button>
                              {r[0]}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[1]}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[2]}</td>
                            {/* Placeholder cells to align with header columns: API, Country, State/Province, County */}
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[3] ?? ''}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[4] ?? ''}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[5] ?? ''}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ (uploadSubSelectedRows && uploadSubSelectedRows[i]) ? Object.values(uploadSubSelectedRows[i]).filter(Boolean).length : 0 }</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, textAlign: 'center', verticalAlign: 'middle' }}>
                              <input style={{ margin: 0, verticalAlign: 'middle' }} type="checkbox" checked={!!uploadSelectedRows[i]} onChange={() => toggleUploadSelection(i)} aria-label={`Select row ${i}`} />
                            </td>
                          </tr>

                          {uploadRowExpanded[i] && (
                            <tr>
                              <td colSpan={7} style={{ padding: 0, borderBottom: 'none', borderTop: 'none' }}>
                                <div style={{ padding: 8, background: 'rgba(255,255,255,0.02)' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', color: '#fff', tableLayout: 'fixed', borderTop: 'none' }}>
                                    <colgroup>
                                      <col style={{ width: '250px' }} />
                                      <col style={{ width: '150px' }} />
                                      <col style={{ width: '150px' }} />
                                      <col style={{ width: '150px' }} />
                                      <col style={{ width: '150px' }} />
                                      <col style={{ width: '150px' }} />
                                      <col style={{ width: '150px' }} />
                                    </colgroup>
                                    <thead>
                                      <tr>
                                        <th style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Curve Name</th>
                                        <th style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Top Depth</th>
                                        <th style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Bottom Depth</th>
                                        <th style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Scale</th>
                                        <th style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Units</th>
                                        <th style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Axis Min</th>
                                        <th style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Axis Max</th>
                                        <th style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>Select</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[0,1,2].map((x) => (
                                        <tr key={x}>
                                          <td style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>GR</td>
                                          <td style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>0</td>
                                          <td style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>5000</td>
                                          <td style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>5 in</td>
                                          <td style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>GAPI</td>
                                          <td style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>0</td>
                                          <td style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>150</td>
                                          <td style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.04)', textAlign: 'center', verticalAlign: 'middle' }}>
                                            <input type="checkbox" checked={!!uploadSubSelectedRows[i]?.[x]} onChange={() => toggleUploadSubSelection(i, x)} />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      ))}
                  </table>
                </div>
              )}
                {/* per-file metadata is now mapped into uploadResultRows (API/Country/State/County) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={() => {
                      setFileSelectedPopupVisible(false);
                      handleUpload();
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: '#fff',
                      color: '#000',
                      cursor: 'pointer'
                    }}
                  >
                    Digitize Selected Curves
                  </button>
                  <button
                    onClick={() => {
                      setFileSelectedPopupVisible(false);
                      setFiles([]);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'transparent',
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>

                <div style={{ color: '#fff', fontSize: 14, textAlign: 'right' }}>
                  test text here
                </div>
              </div>
            </div>

                
            {/* </div> */}
          </>
        )}

        {tinyPopupVisible && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 20,
              width: 50,
              height: 50,
              backgroundColor: '#a4fdff',
              color: '#000',
              textAlign: 'center', verticalAlign: 'middle',
              borderRadius: 4,
              zIndex: 3000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              fontSize: 11,
              textAlign: 'center',
              padding: 4,
            }}
          >
            {tinyPopupText}
          </div>
        )}

        {uploadResultPopupVisible && (
          <>
            <div
              aria-hidden="true"
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.55)',
                zIndex: 4990,
                pointerEvents: 'auto'
              }}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Upload result"
              style={{
                position: 'fixed',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 5000,
                backgroundColor: '#111',
                color: '#fff',
                padding: '18px 20px',
                borderRadius: 8,
                boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
                minWidth: 360,
                maxWidth: '92%',
              }}
            >
              <div style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                Upload result
              </div>

              <div style={{ overflowX: 'auto', minHeight: '600px' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    color: '#fff',
                    textAlign: 'left',
                    tableLayout: 'fixed'
                  }}
                >
                  <colgroup>
                    <col style={{ width: '350px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '120px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 350 }}>Column 1</th>
                      <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>Column 2</th>
                      <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>Column 3</th>
                      <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 120 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                          <div style={{ fontSize: 13, color: '#fff' }}>Select</div>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              aria-label="Select all"
                              checked={uploadResultRows && uploadResultRows.length > 0 && Object.values(uploadSelectedRows).every(Boolean)}
                              onChange={(e) => setAllUploadSelection(e.target.checked)}
                            />
                            <span style={{ color: '#fff', fontSize: 13 }}>Select all</span>
                          </label>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResultRows.length > 0 ? (
                      uploadResultRows.map((r, i) => (
                        <tr key={i}>
                          <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[0]}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[1]}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[2]}</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 120, textAlign: 'center', verticalAlign: 'middle' }}>
                            <input style={{ margin: 0, verticalAlign: 'middle' }} type="checkbox" checked={!!uploadSelectedRows[i]} onChange={() => toggleUploadSelection(i)} aria-label={`Select row ${i}`} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      [0,1,2,3].map(i => (
                        <tr key={i}>
                          <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>&nbsp;</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>&nbsp;</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>&nbsp;</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>&nbsp;</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
                <button
                  onClick={() => {
                    setUploadResultPopupVisible(false);
                    setUploadResultRows([]);
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 6,
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: '#fff',
                    color: '#000',
                    cursor: 'pointer'
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </>
        )}

        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".tif,.tiff,.jpg,.jpeg,.png,.pdf,image/tiff,image/jpeg,image/png,application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <Box sx={{ mt: 15, width: '100%', maxWidth: 'var(--table-max-width)', mx: 'auto', backgroundColor: '#000', p: 2, borderRadius: 2, border: '1px solid rgba(255,255,255,0.04)' }}>
          <div className="dark-data-table-outer" style={{ backgroundColor: '#000' }}>
            <DataTable
              className="dark-data-table"
              title="Your Files"
              columns={columnsWithWidths}
              data={logs}
              // -------- server-side pagination & sorting --------
              pagination
              paginationServer
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[25, 50, 100]}
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
              sortServer
              onSort={handleSort}
              progressPending={loading}
              progressComponent={<div style={{ padding: 16, color: '#fff' }}>Loading…</div>}
              // -------------------------------------------------
              highlightOnHover
              responsive
              fixedHeader
              persistTableHead
              customStyles={tableCustomStyles}
              noDataComponent={
                <div style={{
                  textAlign: 'center', verticalAlign: 'middle',
                  height: 'var(--table-height, 400px)',
                  width: '100%',
                  color: '#fff',
                  backgroundColor: '#000'
                }}>
                  <div style={{ marginTop: '50px', marginLeft: '-480px' }}>
                    No files uploaded yet
                  </div>
                </div>
              }
            />
          </div>
        </Box>

        <Box
          sx={{
            mt: 6,
            width: '100%',
            maxWidth: 'var(--table-max-width)',
            mx: 'auto',
            backgroundColor: 'rgba(255,255,255,0.02)',
            p: 3,
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
              Curve Purchase Summary
            </Typography>
            {invoiceError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {invoiceError}
              </Alert>
            )}
            {invoiceMessage && (
              <Alert severity="success" sx={{ mb: 1 }}>
                {invoiceMessage}
              </Alert>
            )}
            {totalSelectedCurves === 0 ? (
              <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Select curves from the table above to build a quote.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {selectedCurveDetails.map(({ logId, curveIds, curves }) => (
                  <Box
                    key={logId}
                    sx={{
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 1,
                      p: 2,
                      backgroundColor: 'rgba(0,0,0,0.35)',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                        {getLogLabel(logId)}
                      </Typography>
                      <Chip
                        label={`${curveIds.length} ${curveIds.length === 1 ? 'curve' : 'curves'}`}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.12)',
                          color: '#fff',
                          fontWeight: 500,
                        }}
                      />
                    </Stack>
                    {curves.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ mt: 1 }}
                      >
                        {curves.map((curve) => (
                          <Chip
                            key={curve.id}
                            label={curve.name || `Curve ${curve.id}`}
                            variant="outlined"
                            sx={{
                              borderColor: 'rgba(255,255,255,0.3)',
                              color: '#fff',
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button
                variant="contained"
                onClick={handleRequestInvoice}
                disabled={invoiceLoading || totalSelectedCurves === 0}
                sx={{
                  backgroundColor: '#fff',
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#f5f5f5', color: '#000' },
                  '&:disabled': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'rgba(0,0,0,0.4)',
                  },
                  minWidth: 180,
                }}
              >
                Lock Invoice
              </Button>
              {invoiceQuote && (
                <Button
                  variant="outlined"
                  onClick={handleCompletePurchase}
                  disabled={invoiceLoading || invoiceQuote.status === 'completed'}
                  sx={{
                    borderColor: '#fff',
                    color: '#fff',
                    minWidth: 200,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                    '&:disabled': { color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  Confirm Purchase
                </Button>
              )}
              {invoiceLoading && (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              )}
            </Stack>

            {invoiceQuote && (
              <Box
                sx={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: 'rgba(0,0,0,0.4)',
                }}
              >
                <Stack spacing={1}>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                    Invoice ID: {invoiceQuote.external_id}
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Chip
                      label={`Status: ${invoiceQuote.status}`}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        color: '#fff',
                      }}
                    />
                    {invoiceQuote.expires_at && (
                      <Chip
                        label={`Expires ${invoiceExpiryCountdown ? `in ${invoiceExpiryCountdown}` : ''}`.trim()}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.12)',
                          color: '#fff',
                        }}
                      />
                    )}
                  </Stack>
                  {invoiceQuote.expires_at && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Expires at {new Date(invoiceQuote.expires_at).toLocaleString()}
                    </Typography>
                  )}
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1.5 }} />
                  <Stack spacing={0.5}>
                    <Typography sx={{ color: '#fff' }}>
                      Curves: {invoiceQuote.totals?.curve_count ?? 0}
                    </Typography>
                    <Typography sx={{ color: '#fff' }}>
                      Unique logs: {invoiceQuote.totals?.unique_log_count ?? 0}
                    </Typography>
                    <Typography sx={{ color: '#fff' }}>
                      Per-file fee: ${invoiceQuote.totals?.file_fee_total_usd ?? '0.00'}
                    </Typography>
                    <Typography sx={{ color: '#fff' }}>
                      Curve subtotal: ${invoiceQuote.totals?.curve_subtotal_usd ?? '0.00'}
                    </Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                      Total due: ${invoiceQuote.totals?.grand_total_usd ?? '0.00'}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            )}

            {availabilityCountdowns.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                  Purchase availability
                </Typography>
                <Stack spacing={1.5}>
                  {availabilityCountdowns.map((entry) => (
                    <Box
                      key={entry.logId}
                      sx={{
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 1,
                        p: 1.5,
                        backgroundColor: 'rgba(0,0,0,0.35)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                          {getLogLabel(entry.logId)}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          Available after {entry.availableAfter instanceof Date
                            ? entry.availableAfter.toLocaleString()
                            : String(entry.availableAfter)}
                        </Typography>
                      </Stack>
                      <Chip
                        label={entry.ready ? 'Ready' : `Ready in ${entry.countdown}`}
                        sx={{
                          backgroundColor: entry.ready
                            ? 'rgba(76,175,80,0.3)'
                            : 'rgba(255,255,255,0.12)',
                          color: '#fff',
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>

        {files.length > 0 && (
          <List sx={{ mt: 2 }}>
            {files.map((file, idx) => (
              <ListItem key={idx}>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
        )}

        <Button
          variant="contained"
          sx={{
            mt: 2,
            backgroundColor: '#fff',
            color: '#000',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            '&:hover': { backgroundColor: '#f0f0f0' },
            '&:disabled': { backgroundColor: '#ccc', color: '#667' }
          }}
          onClick={() => {
            if (!files || files.length === 0) {
              if (fileInputRef.current) fileInputRef.current.click();
              return;
            }
            handleUpload();
          }}
        >
          Upload New Log(S) for Processing
        </Button>

        <Box sx={{ mt: '75px', width: '100%', maxWidth: 'var(--table-max-width)', mx: 'auto', backgroundColor: '#000', p: 2, borderRadius: 2, border: '1px solid rgba(255,255,255,0.04)' }}>
          <div className="dark-data-table-outer" style={{ backgroundColor: '#000' }}>
            <DataTable
              className="dark-data-table"
              title="Data Available for Immediate Download"
              columns={columnsWithWidths}
              // show server-returned logs in the "Immediate Download" table
              data={logs.map((l) => ({
                id: l.id,
                fileName: l.visible_name || (l.file ? l.file.split('/').pop() : ''),
                status: l.status,
                country: l.country || l.Country || '',
                stateProv: l.state || l.stateProv || '',
                County: l.county || l.County || '',
                API: l.api || l.API || (l.properties && l.properties.api) || '',
                CreatedAt: l.created_at || l.CreatedAt || l.created || null,
              }))}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50]}
              highlightOnHover
              responsive
              fixedHeader
              persistTableHead
              noDataComponent={<div style={{ height: 'var(--table-height, 400px)', textAlign: 'center', verticalAlign: 'middle', color: '#fff' }}>Welcome to the Beta testing of our product. We appreciate your patience as we begin our journey of building a database.</div>}
              customStyles={tableCustomStyles}
            />
          </div>
        </Box>

        <Button
          variant="contained"
          sx={{
            mt: 2,
            backgroundColor: '#fff',
            color: '#000',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            '&:hover': { backgroundColor: '#f0f0f0' },
            '&:disabled': { backgroundColor: '#ccc', color: '#667' }
          }}
          onClick={() => {
            if (!files || files.length === 0) {
              if (fileInputRef.current) fileInputRef.current.click();
              return;
            }
            handleUpload();
          }}
        >
          Upload New Log
        </Button>

        {/* Logs are now shown inside the "Data Available for Immediate Download" table above. */}
      </Container>

      <Dialog open={curveModalOpen} onClose={closeCurveSelector} maxWidth="md" fullWidth>
        <DialogTitle>Select curves for {curveModalLogId ? getLogLabel(curveModalLogId) : 'log'}</DialogTitle>
        <DialogContent dividers>
          {curveModalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {curveModalError}
            </Alert>
          )}
          {curveModalLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : activeCurveList.length === 0 ? (
            <Typography sx={{ color: 'text.secondary' }}>
              No curves are available for this log yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {activeCurveList.map((curve) => {
                const checked = Array.isArray(selectedCurves[curveModalLogId])
                  ? selectedCurves[curveModalLogId].includes(curve.id)
                  : false;
                return (
                  <Box
                    key={curve.id}
                    sx={{
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleCurveSelectionForLog(curveModalLogId, curve.id)}
                        />
                      }
                      label={
                        <Stack spacing={0.5}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {curve.name || `Curve ${curve.id}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Depth: {curve.depth_top ?? '—'} – {curve.depth_bottom ?? '—'} | Unit: {curve.unit || '—'}
                          </Typography>
                          {curve.depth_scale && (
                            <Typography variant="body2" color="text.secondary">
                              Scale: {curve.depth_scale}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => selectAllCurvesForLog(curveModalLogId, activeCurveList, true)}
            disabled={curveModalLoading || activeCurveList.length === 0}
          >
            Select all
          </Button>
          <Button
            onClick={() => selectAllCurvesForLog(curveModalLogId, activeCurveList, false)}
            disabled={curveModalLoading || activeCurveList.length === 0}
          >
            Clear
          </Button>
          <Button onClick={closeCurveSelector}>Close</Button>
        </DialogActions>
      </Dialog>

      {showLoginModal && (
        <div>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(128,128,128,0.6)',
              zIndex: 2000,
              pointerEvents: 'auto'
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              width: '750px',
              height: 'auto',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#fff',
              borderRadius: '12px',
              zIndex: 2100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
              padding: 16
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowLoginModal(false)}
              aria-label="Close dialog"
              style={{
                position: 'absolute',
                right: 6,
                top: 6,
                background: 'transparent',
                border: 'none',
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              ✕
            </button>

            <div style={{ width: '100%', overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  color: '#fff',
                  textAlign: 'left',
                  tableLayout: 'fixed'
                }}
              >
                <colgroup>
                  <col style={{ width: '33%' }} />
                  <col style={{ width: '33%' }} />
                  <col style={{ width: '33%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px' }}>Column 1</th>
                    <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px' }}>Column 2</th>
                    <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px' }}>Column 3</th>
                    <th style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '8px', maxWidth: 100 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all results"
                        checked={uploadResultRows && uploadResultRows.length > 0 && Object.values(uploadSelectedRows).every(Boolean)}
                        onChange={(e) => setAllUploadSelection(e.target.checked)}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResultRows.length > 0 ? (
                    uploadResultRows.map((r, i) => (
                      <tr key={i}>
                        <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{r[0]}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{r[1]}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{r[2]}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <input style={{ margin: 0, verticalAlign: 'middle' }} type="checkbox" checked={!!uploadSelectedRows[i]} onChange={() => toggleUploadSelection(i)} aria-label={`Select row ${i}`} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    [0,1,2,3].map(i => (
                      <tr key={i}>
                        <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>&nbsp;</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>&nbsp;</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>&nbsp;</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>&nbsp;</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                onClick={() => {
                  setFileSelectedPopupVisible(false);
                  handleUpload();
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: '#fff',
                  color: '#000',
                  cursor: 'pointer'
                }}
              >
                Digitize Selected Curves
              </button>

              <button
                onClick={() => {
                  setFileSelectedPopupVisible(false);
                  setFiles([]);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: 'transparent',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>

            <div style={{ marginTop: 20, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ marginTop: 10, textAlign: 'center', verticalAlign: 'middle', gap: 20 }}>
                <label htmlFor="modalC" style={{ color: '#000', fontSize: 16, width: 60, textAlign: 'right' }}>Password</label>
                <input
                  id="modalC"
                  value={modalFieldC}
                  readOnly
                  placeholder='Password not shown for security reasons'
                  style={{
                    width: 480,
                    height: 28,
                    fontSize: 16,
                    backgroundColor: isEditing ? '#ffffffff' : '#bdbdbdff',
                    border: '1px solid #d0d0d0',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}
                />
              </div>

              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  onMouseDown={() => setOkPressed(true)}
                  onMouseUp={() => setOkPressed(false)}
                  onMouseLeave={() => setOkPressed(false)}
                  onTouchStart={() => setOkPressed(true)}
                  onTouchEnd={() => setOkPressed(false)}
                  onClick={() => {
                    setShowLoginModal(false);
                    setTinyPopupText('Saved');
                    setTinyPopupVisible(true);
                    setTimeout(() => setTinyPopupVisible(false), 3000);
                  }}
                  aria-label="Submit modal"
                  style={{
                    marginTop: 8,
                    fontSize: 14,
                    padding: '6px 10px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'transform 80ms, box-shadow 80ms, background-color 120ms',
                    border: '1px solid rgba(0,0,0,0.08)',
                    transform: (copyPressed || isEditing) ? 'translateY(2px)' : 'none',
                    boxShadow: (copyPressed || isEditing) ? 'none' : '0 2px 6px rgba(0,0,0,0.12)',
                    backgroundColor: (copyPressed || isEditing) ? '#e6f7ff' : '#f5f5f5'
                  }}
                >
                  Update Password
                </button>
              </div>

              <div style={{ textAlign: 'center', verticalAlign: 'middle', gap: 10, marginTop: 20 }}>
                <label htmlFor="modalC" style={{ color: '#000', fontSize: 16, width: 80, textAlign: 'right' }}>Credit Card</label>
                <input
                  id="modalC"
                  value={modalFieldC}
                  readOnly
                  placeholder='Password not shown for security reasons'
                  style={{
                    width: 480,
                    height: 28,
                    fontSize: 16,
                    backgroundColor: isEditing ? '#ffffffff' : '#bdbdbdff',
                    border: '1px solid #d0d0d0',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}
                />
              </div>

              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  onMouseDown={() => setOkPressed(true)}
                  onMouseUp={() => setOkPressed(false)}
                  onMouseLeave={() => setOkPressed(false)}
                  onTouchStart={() => setOkPressed(true)}
                  onTouchEnd={() => setOkPressed(false)}
                  onClick={() => {
                    setShowLoginModal(false);
                    setTinyPopupText('Saved');
                    setTinyPopupVisible(true);
                    setTimeout(() => setTinyPopupVisible(false), 3000);
                  }}
                  aria-label="Submit modal"
                  style={{
                    fontSize: 14,
                    padding: '6px 10px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'transform 80ms, box-shadow 80ms, background-color 120ms',
                    border: '1px solid rgba(0,0,0,0.08)',
                    transform: (copyPressed || isEditing) ? 'translateY(2px)' : 'none',
                    boxShadow: (copyPressed || isEditing) ? 'none' : '0 2px 6px rgba(0,0,0,0.12)',
                    backgroundColor: (copyPressed || isEditing) ? '#e6f7ff' : '#f5f5f5'
                  }}
                >
                  Update Credit Card Info
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
